import { ExternalLink, RefreshCcw, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

const accessRows = [
  {
    account: 'Revochem Industries',
    user: 'portal.admin@revochem.example',
    role: 'Admin',
    services: 'Trucking, Depot, Warehouse, Bills & Payments',
    scope: 'Revochem customer account only',
    status: 'Active',
    nextAction: 'Review users and WhatsApp approval links',
  },
  {
    account: 'Revochem Industries',
    user: 'ops@revochem.example',
    role: 'Operations',
    services: 'Trucking, Depot, Warehouse',
    scope: 'Operational rows only',
    status: 'Active',
    nextAction: 'Can create bookings and view container lifecycle',
  },
  {
    account: 'Revochem Industries',
    user: 'finance@revochem.example',
    role: 'Finance',
    services: 'Bills & Payments, Documents & Reports',
    scope: 'Invoices, SOA, Payment Notice, Dispute',
    status: 'Pending',
    nextAction: 'Validate payment notice workflow',
  },
];

const shareRows = [
  { ref: 'SHARE-PN-001', object: 'Payment Notice validation', channel: 'WhatsApp', status: 'Ready', expires: '14 days', owner: 'Finance' },
  { ref: 'SHARE-REP-001', object: 'Repair approval', channel: 'WhatsApp', status: 'Ready', expires: '7 days', owner: 'M&R' },
];

export default function DepotExternalAccessPage() {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">External Access</h2>
          <p className="text-sm text-muted-foreground">
            Customer self-service access is separate from internal depot users. Scope is always customer/service based.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm"><RefreshCcw className="mr-2 h-4 w-4" />Refresh</Button>
          <Button size="sm"><ShieldCheck className="mr-2 h-4 w-4" />Add Customer User</Button>
        </div>
      </div>

      <DepotGridPanel title="Customer Portal Users" subtitle="One row per external login; service grants control visible menus and data scope.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Account</th>
              <th className={depotThClassName}>User</th>
              <th className={depotThClassName}>Role</th>
              <th className={depotThClassName}>Services</th>
              <th className={depotThClassName}>Data Scope</th>
              <th className={depotThClassName}>Status</th>
              <th className={depotThClassName}>Next Action</th>
            </tr>
          </thead>
          <tbody>
            {accessRows.length === 0 ? <DepotEmptyRow colSpan={7}>No customer portal users configured.</DepotEmptyRow> : accessRows.map((row) => (
              <tr key={row.user} className="border-b last:border-0 hover:bg-muted/20">
                <td className={depotTdClassName}>{row.account}</td>
                <td className={`${depotTdClassName} font-mono text-xs`}>{row.user}</td>
                <td className={depotTdClassName}>{row.role}</td>
                <td className={depotTdClassName}>{row.services}</td>
                <td className={depotTdClassName}>{row.scope}</td>
                <td className={depotTdClassName}><Badge variant="outline">{row.status}</Badge></td>
                <td className={depotTdClassName}>{row.nextAction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="WhatsApp / Share Link Approvals" subtitle="Temporary external action links for repair approval, payment validation, and dispute follow-up.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Link</th>
              <th className={depotThClassName}>Object</th>
              <th className={depotThClassName}>Channel</th>
              <th className={depotThClassName}>Status</th>
              <th className={depotThClassName}>Expires</th>
              <th className={depotThClassName}>Owner</th>
            </tr>
          </thead>
          <tbody>
            {shareRows.map((row) => (
              <tr key={row.ref} className="border-b last:border-0 hover:bg-muted/20">
                <td className={`${depotTdClassName} font-mono text-xs`}><ExternalLink className="mr-1 inline h-3.5 w-3.5" />{row.ref}</td>
                <td className={depotTdClassName}>{row.object}</td>
                <td className={depotTdClassName}>{row.channel}</td>
                <td className={depotTdClassName}><Badge variant="secondary">{row.status}</Badge></td>
                <td className={depotTdClassName}>{row.expires}</td>
                <td className={depotTdClassName}>{row.owner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DepotGridPanel>
    </div>
  );
}
